import * as React from 'react';
import { getGetCurrentUserQueryKey, useGetCurrentUser } from '@cdf/api-client-react';

import { useAuth, type AuthIssue } from '@/components/auth/auth-provider';

/**
 * Session bridge.
 *
 * Adapts the real plugin-aware MSAL auth provider (`useAuth`) to the simple
 * session/role contract consumed by the layout shell (sidebar, header,
 * profile menu, RequireAdmin/RequireStaff). This keeps UI components
 * decoupled from MSAL.
 *
 * - Auth plugin disabled → a signed-in "Local Developer" admin so the
 *   template is fully usable in local dev with no identity provider.
 * - Auth plugin enabled  → maps the authenticated Entra account to a session
 *   user and resolves the server-side role from /api/auth/me: tenant
 *   members are staff (admin), Entra guests are customers and get portal
 *   access only.
 */

export type SessionStatus = 'loading' | 'signedIn' | 'signedOut';
export type UserRole = 'admin' | 'member' | 'customer';

export interface SessionUser {
  name: string;
  email: string;
  initials: string;
  role: UserRole;
  avatarUrl?: string;
}

export type Session =
  | { status: 'loading' }
  | { status: 'signedOut' }
  | { status: 'signedIn'; user: SessionUser };

export interface SessionContextValue {
  session: Session;
  authIssue: AuthIssue | null;
  isSigningIn: boolean;
  isSigningOut: boolean;
  recoveryUrl: string;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  clearAuthIssue: () => void;
}

const SessionContext = React.createContext<SessionContextValue>({
  session: { status: 'loading' },
  authIssue: null,
  isSigningIn: false,
  isSigningOut: false,
  recoveryUrl: '',
  signIn: async () => undefined,
  signOut: async () => undefined,
  clearAuthIssue: () => undefined
});

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  const first = parts[0] ?? '';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? '';
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
}

export function UserProvider({ children }: { children: React.ReactNode }) {
  const {
    enabled,
    ready,
    isAuthenticated,
    account,
    issue,
    isSigningIn,
    isSigningOut,
    recoveryUrl,
    provider,
    login,
    logout,
    clearIssue
  } = useAuth();

  // Server-resolved role (staff vs customer guest); only relevant when the
  // auth plugin is enabled and a token is available for the API call.
  const meQuery = useGetCurrentUser({
    query: {
      queryKey: [...getGetCurrentUserQueryKey(), account?.email ?? account?.name ?? 'signed-out'],
      enabled: enabled && ready && isAuthenticated && provider === 'workforce',
      staleTime: 5 * 60 * 1000,
      retry: 1
    }
  });

  const value = React.useMemo<SessionContextValue>(() => {
    let session: Session;

    if (!enabled) {
      // Local dev mode — no identity provider configured.
      session = {
        status: 'signedIn',
        user: {
          name: 'Local Developer',
          email: 'dev@localhost',
          initials: 'LD',
          role: 'admin'
        }
      };
    } else if (!ready) {
      session = { status: 'loading' };
    } else if (isAuthenticated && account) {
      if (provider === 'workforce' && meQuery.isPending) {
        // Hold workforce sessions in loading until the server role is known so
        // customer guests never see a flash of the internal application.
        session = { status: 'loading' };
      } else {
        const name = account.name || account.email || 'User';
        const serverRole = meQuery.data?.role;
        const role: UserRole =
          provider === 'external'
            ? 'customer'
            : serverRole === 'customer'
              ? 'customer'
              : serverRole === 'admin'
                ? 'admin'
                : 'member';
        session = {
          status: 'signedIn',
          user: {
            name,
            email: account.email ?? '',
            initials: initialsOf(name),
            role
          }
        };
      }
    } else {
      session = { status: 'signedOut' };
    }

    return {
      session,
      authIssue: issue,
      isSigningIn,
      isSigningOut,
      recoveryUrl,
      signIn: login,
      signOut: logout,
      clearAuthIssue: clearIssue
    };
  }, [
    enabled,
    ready,
    isAuthenticated,
    account,
    issue,
    isSigningIn,
    isSigningOut,
    recoveryUrl,
    login,
    logout,
    provider,
    clearIssue,
    meQuery.isPending,
    meQuery.data
  ]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  return React.useContext(SessionContext);
}

export function useUser(): SessionUser | null {
  const { session } = useSession();
  return session.status === 'signedIn' ? session.user : null;
}
